import React from 'react';

// Canonical list of the 8 ABDM (M3 certification) HI types - single source of truth.
// Mirrors backend app/abdm/domain/hi_types.py; both sides have a guard test.
export const HI_TYPE_LABELS = {
  OPConsultation: 'OP Consultation',
  Prescription: 'Prescription',
  DiagnosticReport: 'Diagnostic Report',
  DischargeSummary: 'Discharge Summary',
  ImmunizationRecord: 'Immunization Record',
  HealthDocumentRecord: 'Health Document Record',
  WellnessRecord: 'Wellness Record',
  Invoice: 'Invoice',
};

export const HI_TYPES = Object.keys(HI_TYPE_LABELS);

// One line icon per record type, drawn like the app's own icons (components/common/icons: 24-grid,
// 2px round stroke in currentColor), so they follow the text colour in light and dark mode. Sized 1em
// to sit inline with the label. Same keys as HI_TYPE_LABELS - guard test enforces it.
const hiIcon = (type, shapes) => (
  <svg
    data-hi-icon={type} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false"
    style={{ display: 'inline-block', verticalAlign: '-0.125em' }}
  >
    {shapes}
  </svg>
);

export const HI_TYPE_ICONS = {
  // Stethoscope
  OPConsultation: hiIcon('OPConsultation', <>
    <path d="M5 3H4a1 1 0 0 0-1 1v5a5 5 0 0 0 10 0V4a1 1 0 0 0-1-1h-1" />
    <path d="M8 14v1a6 6 0 0 0 12 0v-3" />
    <circle cx="20" cy="10" r="2" />
  </>),
  // Capsule
  Prescription: hiIcon('Prescription', <>
    <path d="M10.5 20.5l10-10a4.95 4.95 0 0 0-7-7l-10 10a4.95 4.95 0 0 0 7 7z" />
    <path d="M8.5 8.5l7 7" />
  </>),
  // Test tube
  DiagnosticReport: hiIcon('DiagnosticReport', <>
    <path d="M14.5 2v17.5a2.5 2.5 0 0 1-5 0V2" />
    <path d="M8.5 2h7" />
    <path d="M9.5 15h5" />
  </>),
  // Medical cross in a square
  DischargeSummary: hiIcon('DischargeSummary', <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M12 8v8M8 12h8" />
  </>),
  // Syringe
  ImmunizationRecord: hiIcon('ImmunizationRecord', <>
    <path d="M18 2l4 4" />
    <path d="M17 7l3-3" />
    <path d="M19 9L8.7 19.3a2.4 2.4 0 0 1-3.4 0l-.6-.6a2.4 2.4 0 0 1 0-3.4L15 5" />
    <path d="M9 11l4 4" />
    <path d="M5 19l-3 3" />
    <path d="M14 4l6 6" />
  </>),
  // Page with lines
  HealthDocumentRecord: hiIcon('HealthDocumentRecord', <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h8" />
  </>),
  // Pulse line
  WellnessRecord: hiIcon('WellnessRecord', <path d="M22 12h-4l-3 9L9 3l-3 9H2" />),
  // Receipt with a zigzag bottom
  Invoice: hiIcon('Invoice', <>
    <path d="M5 2h14v20l-2.33-1.5L14.33 22 12 20.5 9.67 22l-2.34-1.5L5 22z" />
    <path d="M9 7h6M9 11h6M9 15h4" />
  </>),
};

/** ABDM display name for a record type; unknown codes pass through unchanged. */
export const hiTypeLabel = (type) => HI_TYPE_LABELS[type] || type;

// ── Upload rules (build-plan §4.3; the server enforces the same) ──────────────────────────────
// Single source for the Upload / Replace drawer's client-side checks - never repeat the numbers.
export const TYPE_PRESCRIPTION = 'Prescription';
export const TYPE_DIAGNOSTIC = 'DiagnosticReport';
// A1: an Invoice is a multi-instance slot like Diagnostic Report, but it links through its own route
// (the records route refuses its slot key), so it is never part of a records "Link all".
export const TYPE_INVOICE = 'Invoice';

// The types that can hold several slots on one visit (A1.1: slotKey differs from the type); every other type
// holds at most one. The record-type strip shows a count and "+" for these.
export const MULTI_SLOT_TYPES = Object.freeze([TYPE_DIAGNOSTIC, TYPE_INVOICE]);

// OR-18: these two types take ONE file (staff join pages themselves); the other types take several.
export const SINGLE_FILE_TYPES = Object.freeze([TYPE_PRESCRIPTION, TYPE_DIAGNOSTIC]);

const MB = 1024 * 1024;
export const UPLOAD_LIMITS = Object.freeze({
  filesPerUpload: 5, // files in one pick
  filesPerRecord: 10, // active files on one slot
  fileBytes: 10 * MB, // one file
  recordBytes: 25 * MB, // all active files of one slot
});
